import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ViewType } from 'store/GeneralState/GeneralState.reducer'
import { AppState } from 'store/store'
import { hasStatisticsConsent, isEtrackerConfig, trackOrderConversion, trackVzfOrderNumber } from 'utils/etracker'
import { basketCalculation } from 'utils/testable/basketCalculation'
import useURLParams from 'utils/URLParamsContex'

function EtrackerRouteTracker(): null {
    const { t } = useTranslation()
    const { B2B } = useURLParams()
    const location = useLocation()
    const { availabilityCheck, bankDetails, generalState, orderID, vzfID, etrackerConfiguration } = useSelector(
        (state: AppState) => ({
            availabilityCheck: state.availabilityCheck,
            bankDetails: state.bankDetails,
            generalState: state.generalState,
            orderID: state.generalState.orderID,
            vzfID: state.generalState.vzfID,
            etrackerConfiguration: state.generalState.customizeJsData?.etrackerConfiguration,
        }),
    )
    const lastVzfTrackedRef = useRef('')
    const lastOrderConversionTrackedRef = useRef('')

    useEffect(() => {
        if (!isEtrackerConfig(etrackerConfiguration) || !hasStatisticsConsent()) {
            return
        }

        const vzfViewPath = '/' + t(ViewType.VZF_VIEW)
        const vzfLandingPath = '/' + t(ViewType.VZF_LANDINGPAGE)
        const isVzfRoute = location.pathname === vzfViewPath || location.pathname === vzfLandingPath

        if (!isVzfRoute || !vzfID) {
            return
        }

        const trackKey = `${location.pathname}:${vzfID}`
        if (lastVzfTrackedRef.current === trackKey) {
            return
        }
        lastVzfTrackedRef.current = trackKey

        trackVzfOrderNumber(etrackerConfiguration.secureCode, vzfID)
    }, [etrackerConfiguration, location.pathname, t, vzfID])

    useEffect(() => {
        if (!isEtrackerConfig(etrackerConfiguration) || !hasStatisticsConsent()) {
            return
        }

        const orderConfirmationPath = '/' + t(ViewType.ORDER_CONFIRMATION)
        if (location.pathname !== orderConfirmationPath) {
            return
        }

        const orderNumber = orderID || vzfID
        if (!orderNumber) {
            return
        }

        if (lastOrderConversionTrackedRef.current === orderNumber) {
            return
        }
        lastOrderConversionTrackedRef.current = orderNumber

        const basket = basketCalculation(availabilityCheck, bankDetails, generalState, B2B, false)
        const orderValue = (basket.costs?.monthlyCost ?? 0) + (basket.costs?.oneTimeCost ?? 0)

        trackOrderConversion(etrackerConfiguration.secureCode, orderNumber, orderValue)
    }, [availabilityCheck, bankDetails, B2B, etrackerConfiguration, generalState, location.pathname, orderID, t, vzfID])

    return null
}

export default EtrackerRouteTracker
